/**
 * The Achievement catalogue as this app presents it: names, descriptions and the order the profile
 * draws them in. zl-api owns which achievements exist and how each is earned
 * (`apps/api/src/models/core/achievements.ts`); this copy is keyed by the same `key`, and must move
 * with it. A key the API grows later is ignored here until this app learns how to draw it.
 */
export type AchievementKey =
  | 'new-arrival'
  | 'first-visit'
  | 'first-sight'
  | 'super-sight'
  | 'superb-sight'
  | 'splendid-sight'
  | 'community-voice'
  | 'community-journalist'
  | 'community-chronicler'
  | 'habitat-champion';

type AchievementGroup = 'new-arrival' | 'first-visit' | 'ugc-taken' | 'community-interaction' | 'champion';

interface AchievementDefinition {
  key: AchievementKey;
  group: AchievementGroup;
  tier: number;
  name: string;
  description: string;
  /** What holding it unlocks, said in bold after the description. */
  perk?: string;
  /** Hidden from everyone, its holder included, until it is held. */
  isSecret?: boolean;
}

const COMMUNITY_DESCRIPTION = 'Earned when you support others’ clips and videos of the animals.';

const ACHIEVEMENTS: readonly AchievementDefinition[] = [
  {
    key: 'new-arrival',
    group: 'new-arrival',
    tier: 1,
    name: 'New Arrival',
    description: 'Earned when you complete your profile customisations! Welcome!',
  },
  {
    key: 'first-visit',
    group: 'first-visit',
    tier: 1,
    name: 'First Visit',
    description: 'Earned each time you meet a new habitat. Welcome!',
  },
  {
    key: 'first-sight',
    group: 'ugc-taken',
    tier: 1,
    name: 'First Sight',
    description: 'Earned when you take your first pic or clip of an animal in their Live habitat!',
  },
  {
    key: 'super-sight',
    group: 'ugc-taken',
    tier: 2,
    name: 'Super Sight',
    description: 'Earned when you take 5 pics or clips of an animal in their Live habitat!',
  },
  {
    key: 'superb-sight',
    group: 'ugc-taken',
    tier: 3,
    name: 'Superb Sight',
    description: 'Earned when you take 10 pics or clips of an animal in their Live habitat!',
  },
  {
    key: 'splendid-sight',
    group: 'ugc-taken',
    tier: 4,
    name: 'Splendid Sight',
    description: 'Earned when you take 20 pics or clips of an animal in their Live habitat!',
  },
  {
    key: 'community-voice',
    group: 'community-interaction',
    tier: 1,
    name: 'Community Voice',
    description: COMMUNITY_DESCRIPTION,
  },
  {
    key: 'community-journalist',
    group: 'community-interaction',
    tier: 2,
    name: 'Community Journalist',
    description: COMMUNITY_DESCRIPTION,
  },
  {
    key: 'community-chronicler',
    group: 'community-interaction',
    tier: 3,
    name: 'Community Chronicler',
    description: COMMUNITY_DESCRIPTION,
  },
  {
    key: 'habitat-champion',
    group: 'champion',
    tier: 1,
    name: 'Habitat Champion',
    description: 'We see you! You make habitats a better place for everyone to follow the animals’ stories.',
    perk: 'Enjoy a custom gradient for your profile!',
    isSecret: true,
  },
];

const GROUP_ORDER: readonly AchievementGroup[] = [
  'new-arrival',
  'first-visit',
  'ugc-taken',
  'community-interaction',
  'champion',
];

/** An Achievement as the API returns it on a user or a profile. */
export interface UnlockedAchievement {
  key: string;
  habitatId?: string;
  unlockedAt: string;
}

interface AchievementSlot {
  group: AchievementGroup;
  /** The highest tier held, or tier 1 when the group is locked. */
  key: AchievementKey;
  held: boolean;
  /** First Visit only: every habitat met, newest first. */
  habitatIds: string[];
}

export const findAchievement = (key: string | null | undefined) =>
  ACHIEVEMENTS.find((achievement) => achievement.key === key);

/** One slot per group, in group order. */
const slotPerGroup = (held: readonly UnlockedAchievement[] | undefined): AchievementSlot[] =>
  GROUP_ORDER.flatMap((group): AchievementSlot[] => {
    const tiers = ACHIEVEMENTS.filter((achievement) => achievement.group === group);
    const heldTiers = tiers.filter(({ key }) => held?.some((achievement) => achievement.key === key));
    const highest = heldTiers.reduce<AchievementDefinition | undefined>(
      (best, tier) => (!best || tier.tier > best.tier ? tier : best),
      undefined,
    );
    const [firstTier] = tiers;

    if (!firstTier || (!highest && firstTier.isSecret)) {
      return [];
    }

    const habitatIds = (held ?? [])
      .filter((achievement) => achievement.key === 'first-visit' && achievement.habitatId)
      .sort((a, b) => b.unlockedAt.localeCompare(a.unlockedAt))
      .map(({ habitatId }) => habitatId as string);

    return [
      { group, key: (highest ?? firstTier).key, held: !!highest, habitatIds: group === 'first-visit' ? habitatIds : [] },
    ];
  });

/**
 * What the profile draws: one slot per Achievement Group, showing the highest Tier held — or tier 1,
 * locked, when nothing in the group is held; a secret group shows nothing until it is held. What is
 * held comes first, each half in group order, so earned achievements lead the row.
 */
export const achievementSlots = (held: readonly UnlockedAchievement[] | undefined): AchievementSlot[] => {
  const slots = slotPerGroup(held);
  return [...slots.filter((slot) => slot.held), ...slots.filter((slot) => !slot.held)];
};

/**
 * What Mixpanel gets instead of the raw list, on every event and on the profile: the keys held, each
 * once, and how many habitats have been met. Both stay the same size however many habitats a user
 * meets. Mirrors zl-api's `summariseAchievements`, which writes the same two properties server-side.
 */
export const summariseAchievements = (held: readonly UnlockedAchievement[] | undefined) => ({
  achievements: [...new Set((held ?? []).map(({ key }) => key))],
  uniqueHabitatsVisited: (held ?? []).filter(({ key }) => key === 'first-visit').length,
});
